/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CustomCardList
 *
 * @file CustomCardList.jsx
 * @author Mohamed yasar arabath M
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  makeStyles
} from '@material-ui/core';
import mixins from '../../assets/styles/mixins.scss';
import CustomCard from './CustomCard';
import CustomScrollbar from '../CustomScrollbar/CustomScrollbar';
import { BadgeTemplate } from '../tabular/Tabular';

const useStyles = makeStyles(() => ({
  inner: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    alignItems: 'flex-start',
    width: '100%',
    maxHeight: 'var(--custom-card-list-max-height)',
    borderRadius: '16px'
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 24,
    width: '100%',
    justifyContent: 'center'
  },
  title: {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    lineHeight: '22px',
    fontWeight: mixins.fontWeight600,
    marginBottom: 16
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    width: '100%',
    position: 'relative',
    flex: 1,
    overflowY: 'auto'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    width: '100%',
    position: 'relative',
    flex: 1,
    overflowY: 'auto'
  },
  listItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%'
  },
  listText: {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    lineHeight: '20px',
    fontWeight: mixins.fontWeight400,
    flex: 1,
    textAlign: 'left'
  },
  chip: {
    color: mixins.accentRed
  },
  listWithBadge: {
    gap: 8
  },
  listWithoutBadge: {
    gap: 2
  }
}));
export default function CustomCardList({ title, items, isBadgeUsed, customStyles, status }) {
  const classes = useStyles();

  return (
    <CustomCard classes={{ root: classes.inner }}>
      <CustomScrollbar autoHeight>
        <Typography className={classes.title}>
          {title}
        </Typography>

        <div className={classes.body}>
          <div className={classes.list} style={{ gap: isBadgeUsed ? 8 : 2 }}>
            {items.map((item, idx) => (
              <div key={idx} className={classes.listItem}>
                {isBadgeUsed ? (
                  <BadgeTemplate
                    topics={[item]}
                    classes={{
                      chip: customStyles?.chip || classes.chip
                    }}
                    status={status}
                  />
                ) : (
                  <Typography className={classes.listText}>
                    {item}
                  </Typography>
                )}
              </div>
            ))}
          </div>
        </div>
      </CustomScrollbar>
    </CustomCard>
  );
}
CustomCardList.defaultProps = {
  isBadgeUsed: false,
  customStyles: '',
  status: ''
};

CustomCardList.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  isBadgeUsed: PropTypes.bool,
  customStyles: PropTypes.string,
  status: PropTypes.string
};
